/* eslint-disable @angular-eslint/directive-selector */
import { AfterViewInit, DestroyRef, Directive, ElementRef, HostListener, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { KeyboardModeService } from './keyboard-mode.service';

/**
 * Directive to handle keyboard-only focus outline visibility
 *
 * This directive tracks the input modality (mouse vs keyboard) and applies
 * a 'lx-keyboard-focus' class only when focus is achieved via keyboard navigation.
 * This is necessary because browsers apply :focus-visible to form inputs even
 * on mouse clicks, which we want to prevent.
 *
 * The directive automatically applies to:
 * - Native form elements: input, select, textarea (including radio and checkbox) and lx-button
 * - PrimeNG component wrappers and their nested inputs
 * - Elements with explicit [lxKeyboardFocus] attribute
 *
 * @example
 * ```html
 * <!-- Automatically applies to these elements -->
 * <input pInputText />
 * <input type="checkbox" />
 * <textarea></textarea>
 * <p-inputMask /> <!-- Also applies to nested input inside -->
 * <p-dropdown /> <!-- Also applies to nested elements inside -->
 * <p-checkbox /> <!-- Also applies to checkbox component -->
 *
 * <!-- Manual application -->
 * <div contenteditable="true" lxKeyboardFocus></div>
 * ```
 */
@Directive({
  selector:
    'input, textarea, select, p-inputMask, p-dropdown, p-radioButton, p-checkbox, p-calendar, p-multiselect, p-tree, p-inputNumber, [pInputText], [lxKeyboardFocus], lx-button',
  standalone: true,
})
export class KeyboardFocusDirective implements AfterViewInit {
  private readonly elementRef = inject(ElementRef);
  private readonly renderer = inject(Renderer2);
  private readonly keyboardModeService = inject(KeyboardModeService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly KeyboardFocusCssClass = 'lx-keyboard-focus';

  ngAfterViewInit(): void {
    // Subscribe to keyboard mode changes to handle components created while keyboard mode is active
    this.keyboardModeService.isKeyboardMode$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((keyboardModeEnabled) => {
      // If element currently has focus and keyboard mode changed, update styling
      if (this.elementRef.nativeElement === document.activeElement || this.elementRef.nativeElement.contains(document.activeElement)) {
        const focusedElement = document.activeElement as HTMLElement;

        if (keyboardModeEnabled) {
          this.renderer.addClass(focusedElement, this.KeyboardFocusCssClass);
          if (this.isElementStartsWith('p-') || this.isElementStartsWith('lx-button')) {
            this.renderer.addClass(this.elementRef.nativeElement, this.KeyboardFocusCssClass);
          }
        } else {
          this.renderer.removeClass(focusedElement, this.KeyboardFocusCssClass);
          if (this.isElementStartsWith('p-') || this.isElementStartsWith('lx-button')) {
            this.renderer.removeClass(this.elementRef.nativeElement, this.KeyboardFocusCssClass);
          }
        }
      }
    });
  }

  /**
   * Handle focus event on the element
   * Add 'lx-keyboard-focus' class only if focus came from keyboard navigation
   */
  @HostListener('focus', ['$event'])
  @HostListener('focusin', ['$event'])
  protected onFocus(event: FocusEvent): void {
    // Determine the actual focused element
    const focusedElement = event.target as HTMLElement;
    const keyboardModeEnabled = this.keyboardModeService.isKeyboardMode;

    if (keyboardModeEnabled) {
      // Add class to the actual focused element
      this.renderer.addClass(focusedElement, this.KeyboardFocusCssClass);

      // Also add to the host element if it's a PrimeNG component
      if (this.isElementStartsWith('p-') || this.isElementStartsWith('lx-button')) {
        this.renderer.addClass(this.elementRef.nativeElement, this.KeyboardFocusCssClass);
      }
    }
  }

  /**
   * Handle blur event on the element
   * Remove 'lx-keyboard-focus'
   * class when element loses focus
   */
  @HostListener('blur', ['$event'])
  @HostListener('focusout', ['$event'])
  protected onBlur(event: FocusEvent): void {
    // Determine the actual blurred element
    const blurredElement = event.target as HTMLElement;

    // Remove class from the actual blurred element
    this.renderer.removeClass(blurredElement, this.KeyboardFocusCssClass);

    // Also remove from the host element if it's a PrimeNG component or a KNG button
    if (this.isElementStartsWith('p-') || this.isElementStartsWith('lx-button')) {
      this.renderer.removeClass(this.elementRef.nativeElement, this.KeyboardFocusCssClass);
    }
  }

  private isElementStartsWith(str: string): boolean {
    return this.elementRef.nativeElement.tagName.toLowerCase().startsWith(str);
  }
}
